import React, { useEffect, useState, useCallback } from 'react';
import styled from "styled-components";
import ReactDOM from "react-dom";
import MyHeader from "../../components/common/MyHeader";
import ActionBar from "../../components/common/ActionBar";
import { addDays, startOfDay, startOfWeek, differenceInWeeks, addWeeks } from 'date-fns';
import { format, parseISO } from 'date-fns';
import DailyView from "./DailyView";
import WeeklyView from "./WeeklyView";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '../../contexts/AuthContext';
import { getDraft, deleteDraft } from '../../firebase';
import AddActivityWindow from "../../components/AddActivityWindow";
import { getTeamAllSessions } from '../../firebase';
import PendingRatingsAlert from '../../components/PendingRatingsAlert';
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

const Activities = ({ isAdminView = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { groupInfo, user } = useAuth();
    const [claimDraftId, setClaimDraftId] = useState(null);
    const [claimDraftData, setClaimDraftData] = useState(null);
    const daysToShow = 5;
    const [sessions, setSessions] = useState([]);
    const [today, setToday] = useState(() => startOfDay(new Date()));
    const [startDate, setStartDate] = useState(() => {
        const todayDate = startOfDay(new Date());
        
        // If no groupInfo, default to today-4
        if (!groupInfo || !groupInfo.startDate) {
            return addDays(todayDate, -daysToShow + 1);
        }
        
        const projectStartDate = startOfDay(groupInfo.startDate);
        const projectEndDate = groupInfo.endDate ? startOfDay(groupInfo.endDate) : null;
        
        // Check if today is within project period
        const isTodayAfterStart = todayDate >= projectStartDate;
        const isTodayBeforeEnd = !projectEndDate || todayDate <= projectEndDate;
        
        // If today is within the project period, show today (centered)
        if (isTodayAfterStart && isTodayBeforeEnd) {
            return addDays(todayDate, -daysToShow + 1);
        }
        
        return projectStartDate;
    });
    const [weekNum, setWeekNum] = useState(() => {
        if (!groupInfo) return 0;
        const groupStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        
        let referenceDate = today;
        if (groupInfo.endDate) {
            const projectEndDate = startOfDay(groupInfo.endDate);
            // If project has ended, start from Week 1
            if (today > projectEndDate) {
                return 0;
            }
        }
        
        const referenceWeek = startOfWeek(referenceDate, { weekStartsOn: 1 });
        const weekDifference = differenceInWeeks(referenceWeek, groupStartWeek);

        const pageNum = Math.floor(weekDifference / 5) * 5;
        return Math.max(0, pageNum);
    });
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [currentView, setCurrentView] = useState('Day');

    useBodyScrollLock(!!claimDraftData);

    const calculateCurrentWeekNum = () => {
        if (!groupInfo) return 0;
        const groupStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        
        let referenceDate = today;
        if (groupInfo.endDate) {
            const projectEndDate = startOfDay(groupInfo.endDate);
            // If project has ended, go to Week 1
            if (today > projectEndDate) {
                return 0;
            }
        }
        
        const referenceWeek = startOfWeek(referenceDate, { weekStartsOn: 1 });
        const weekDifference = differenceInWeeks(referenceWeek, groupStartWeek);
        const pageNum = Math.floor(weekDifference / 5) * 5;
        return Math.max(0, pageNum);
    };

    // Handle week click navigation
    const handleWeekClick = (clickedWeekNum) => {
        if (!groupInfo || !groupInfo.startDate) return;
        
        // Calculate the start date of the clicked week (Monday)
        const courseStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        const targetWeekStart = addWeeks(courseStartWeek, clickedWeekNum - 1);
        
        // Set the startDate to show the clicked week in Day view
        setStartDate(targetWeekStart);
        
        // Switch to Day view
        setCurrentView('Day');
    };

    const handleViewChange = (newView) => {
        if (newView === 'Week' && currentView !== 'Week') {
            const newWeekNum = calculateCurrentWeekNum();
            setWeekNum(newWeekNum);
        }
        setCurrentView(newView);
    };

    useEffect(() => {
        const updateDate = () => {
            const newToday = startOfDay(new Date());
            if (newToday.getTime() !== today.getTime()) {
                setToday(newToday);
                setStartDate(addDays(newToday, -daysToShow + 1));
                if (currentView === 'Week') {
                    setWeekNum(calculateCurrentWeekNum());
                }
            }
        };
        const intervalId = setInterval(updateDate, 60000);
        return () => clearInterval(intervalId);
    }, [today, daysToShow, currentView]);

    const goToToday = () => {
        if (currentView === 'Day') {
            // Check if today is within project period
            if (groupInfo && groupInfo.startDate) {
                const projectStartDate = startOfDay(groupInfo.startDate);
                const projectEndDate = groupInfo.endDate ? startOfDay(groupInfo.endDate) : null;
                
                const isTodayAfterStart = today >= projectStartDate;
                const isTodayBeforeEnd = !projectEndDate || today <= projectEndDate;
                
                if (isTodayAfterStart && isTodayBeforeEnd) {
                    setStartDate(addDays(today, -daysToShow + 1));
                } else {
                    setStartDate(projectStartDate);
                }
            } else {
                setStartDate(addDays(today, -daysToShow + 1));
            }
        } else if (currentView === 'Week') {
            setWeekNum(calculateCurrentWeekNum());
        }
    };

    const handleMemberSelect = (member) => {
        setSelectedMembers(prevSelected => {
            if (prevSelected.includes(member)) {
                return prevSelected.filter(m => m !== member);
            } else {
                return [...prevSelected, member];
            }
        });
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('refresh')) {
            searchParams.delete('refresh');
            navigate(location.pathname + '?' + searchParams.toString(), { replace: true });
            window.location.reload();
        }
    }, [location, navigate]);

    const discordClaimParam = location.search ? new URLSearchParams(location.search).get('discordClaim') : null;
    const groupId = groupInfo?.groupId;

    useEffect(() => {
        if (!groupInfo || !discordClaimParam || !user) return;
        let cancelled = false;
        getDraft(discordClaimParam)
            .then((draft) => {
                if (cancelled || !draft || draft.claimed) return;
                const msgs = draft.messages ?? [];
                const dates = msgs.map((m) => {
                    const t = m.timestamp;
                    return t ? (typeof t === "string" ? parseISO(t) : new Date(t)) : null;
                }).filter(Boolean);
                const minDate = dates.length > 0 ? new Date(Math.min(...dates.map((d) => d.getTime()))) : new Date();
                const maxDate = dates.length > 0 ? new Date(Math.max(...dates.map((d) => d.getTime()))) : new Date();
                const formDate = format(minDate, "yyyy-MM-dd");
                const formEndDate = format(maxDate, "yyyy-MM-dd");
                const isCrossDate = formDate !== formEndDate;
                setClaimDraftData({
                    draft,
                    discordDraftPayload: {
                        channelName: draft.channelName ?? draft.channelId,
                        channelId: draft.channelId,
                        guildId: draft.guildId,
                        messages: draft.messages ?? [],
                        date: formDate,
                        startTime: format(minDate, "HH:mm"),
                        endTime: format(maxDate, "HH:mm"),
                        isCrossDate,
                    },
                    formDate,
                });
                setClaimDraftId(discordClaimParam);
            })
            .catch((err) => {
                if (!cancelled) console.error("Error fetching claim draft:", err);
            });
        return () => { cancelled = true; };
    }, [groupId, discordClaimParam, user]);

    const closeClaimForm = useCallback(() => {
        setClaimDraftData(null);
        setClaimDraftId(null);
        const params = new URLSearchParams(location.search);
        params.delete('discordClaim');
        const qs = params.toString();
        navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    }, [location, navigate]);

    const handleClaimSubmit = useCallback(async (newSession) => {
        if (claimDraftId && user?.email) {
            try {
                await deleteDraft(claimDraftId);
            } catch (err) {
                console.error("Error claiming draft:", err);
            }
        }
        closeClaimForm();
        navigate('/activities?refresh=1', { replace: true });
        window.location.reload();
    }, [claimDraftId, user?.email, closeClaimForm, navigate]);
    useEffect(() => {
        async function fetchSessions() {
            if (!groupInfo?.groupId) {
                return;
            }
            try {
                const allSessions = await getTeamAllSessions(groupInfo.groupId);
                setSessions(allSessions);
            } catch (error) {
                console.error("Error fetching team sessions:", error);
            }
        }

        fetchSessions();
    }, [groupInfo]);

    return (
        <>
            <MyHeader
                alertComponent={!isAdminView ? <PendingRatingsAlert sessions={sessions} compact /> : null}
            />
            <PageWrapper>

                <ActionBar
                    weekNum={weekNum}
                    setWeekNum={setWeekNum}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    goToToday={goToToday}
                    onMemberSelect={handleMemberSelect}
                    selectedMembers={selectedMembers}
                    currentView={currentView}
                    setCurrentView={handleViewChange}
                    daysToShow={daysToShow}
                />

                {currentView === 'Day' && (
                    <DailyView
                        startDate={startDate}
                        setStartDate={setStartDate}
                        daysToShow={daysToShow}
                        today={today}
                        selectedMembers={selectedMembers}
                        isAdminView={isAdminView}
                    />
                )}
                {currentView === 'Week' && (
                    <WeeklyView
                        weekNum={weekNum}
                        today={today}
                        selectedMembers={selectedMembers}
                        isAdminView={isAdminView}
                        onWeekClick={handleWeekClick}
                    />
                )}
            </PageWrapper>

            {claimDraftData && ReactDOM.createPortal(
                <ClaimOverlay onClick={closeClaimForm}>
                    <div onClick={(e) => e.stopPropagation()}>
                        <AddActivityWindow
                            onCancel={closeClaimForm}
                            onCloseSilently={closeClaimForm}
                            date={claimDraftData.formDate}
                            onSubmit={handleClaimSubmit}
                            isEditMode={false}
                            sessionData={null}
                            allowDescriptionOnly={true}
                            initialTitle=""
                            initialDescription=""
                            discordDraftData={claimDraftData.discordDraftPayload}
                        />
                    </div>
                </ClaimOverlay>,
                document.body
            )}
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    padding: 15px 20px 80px;
`;

const ClaimOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
`;


export default Activities;
